import { useCallback, useEffect, useState } from 'react';

import { supabase } from '../../../lib/supabase';

/** Mirrors `public.job_preferences` (WO-20). */
export interface JobPreferences {
  user_id: string;
  skills: string[];
  domain: string | null;
  job_type: string | null;
  experience_level: string | null;
  location: string | null;
  remote_preferred: boolean;
  salary_min: number | null;
  salary_max: number | null;
  salary_currency: string;
  updated_at: string;
}

function mapRow(row: Record<string, unknown> | null): JobPreferences | null {
  if (!row) return null;
  return {
    user_id: row.user_id as string,
    skills: Array.isArray(row.skills) ? (row.skills as string[]) : [],
    domain: (row.domain as string | null) ?? null,
    job_type: (row.job_type as string | null) ?? null,
    experience_level: (row.experience_level as string | null) ?? null,
    location: (row.location as string | null) ?? null,
    remote_preferred: Boolean(row.remote_preferred),
    salary_min:
      row.salary_min === null || row.salary_min === undefined
        ? null
        : Number(row.salary_min),
    salary_max:
      row.salary_max === null || row.salary_max === undefined
        ? null
        : Number(row.salary_max),
    salary_currency: (row.salary_currency as string) ?? 'INR',
    updated_at: (row.updated_at as string) ?? new Date().toISOString(),
  };
}

export function useJobPreferences(): {
  prefs: JobPreferences | null;
  loading: boolean;
  saving: boolean;
  save: (p: Partial<JobPreferences>) => Promise<void>;
  error: string | null;
  isSignedIn: boolean;
} {
  const [prefs, setPrefs] = useState<JobPreferences | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSignedIn, setIsSignedIn] = useState(false);

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (!session) {
          if (mounted) {
            setIsSignedIn(false);
            setPrefs(null);
          }
          return;
        }
        if (mounted) setIsSignedIn(true);

        const { data, error: qErr } = await supabase
          .from('job_preferences')
          .select('*')
          .eq('user_id', session.user.id)
          .maybeSingle();

        if (!mounted) return;
        if (qErr) throw qErr;
        setPrefs(mapRow(data as Record<string, unknown> | null));
      } catch (e) {
        if (mounted) {
          setError(e instanceof Error ? e.message : 'Could not load job preferences.');
          setPrefs(null);
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };

    void load();
    return () => {
      mounted = false;
    };
  }, []);

  const save = useCallback(
    async (patch: Partial<JobPreferences>) => {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session) {
        setError('You must be signed in to save.');
        return;
      }

      setSaving(true);
      setError(null);
      try {
        const prev = prefs;
        const merged: Record<string, unknown> = {
          user_id: session.user.id,
          skills: patch.skills ?? prev?.skills ?? [],
          domain: patch.domain !== undefined ? patch.domain : (prev?.domain ?? null),
          job_type:
            patch.job_type !== undefined ? patch.job_type : (prev?.job_type ?? null),
          experience_level:
            patch.experience_level !== undefined
              ? patch.experience_level
              : (prev?.experience_level ?? null),
          location:
            patch.location !== undefined ? patch.location : (prev?.location ?? null),
          remote_preferred:
            patch.remote_preferred !== undefined
              ? patch.remote_preferred
              : (prev?.remote_preferred ?? false),
          salary_min:
            patch.salary_min !== undefined
              ? patch.salary_min
              : (prev?.salary_min ?? null),
          salary_max:
            patch.salary_max !== undefined
              ? patch.salary_max
              : (prev?.salary_max ?? null),
          salary_currency:
            patch.salary_currency !== undefined
              ? patch.salary_currency
              : (prev?.salary_currency ?? 'INR'),
          updated_at: new Date().toISOString(),
        };

        const { data, error: upErr } = await supabase
          .from('job_preferences')
          .upsert(merged, { onConflict: 'user_id' })
          .select('*')
          .single();

        if (upErr) throw upErr;
        setPrefs(mapRow(data as Record<string, unknown>));
        setError(null);
      } catch (e) {
        const msg = e instanceof Error ? e.message : 'Could not save job preferences.';
        setError(msg);
        throw e instanceof Error ? e : new Error(msg);
      } finally {
        setSaving(false);
      }
    },
    [prefs],
  );

  return { prefs, loading, saving, save, error, isSignedIn };
}
